import { useState, useCallback, useEffect } from "react";
import Results from "../results/Results";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaceCustomEvent,
  ControlEventInstruction,
} from "@innovatrics/dot-face-auto-capture/events";
import { OnBoardStep } from "../../types/onboard";
import styles from "../../styles/onboard.module.css";
import StepTracker from "../common/StepTracker";
import { dispatchControlEvent } from "@innovatrics/dot-face-auto-capture/events";
import {
  onboardSteps,
} from "../../utils/helper";
import { CustomerDetailsProvider } from "../../provider/CustomerDetailsProvider";
import PrimaryDocumentCapture from "./document/PrimaryDocumentCapture";
import Canvas from "../common/Canvas";
import SecondaryDocumentCapture from "./document/secondary/SecondaryDocumentCapture";
import LivenessCapture from "./liveness/LivenessCapture";
import useIdleSession from "../../hooks/useIdleSession";
import Modal from "../modal/Modal";
import { useSessionProvider } from "../../provider/SessionProvider";
import { useClient } from "../../provider/ClientProvider";
import { useClientSession } from "../../provider/ClientSessionProvider";
import { modalType } from "../../types/modal";

const Onboard = () => {
  const {setIsOpen,isOpen} = useSessionProvider();
  // IDV-4: the secondary document is only collected when the partner's IwC
  // configuration asks for it. When it is off the step is skipped entirely and
  // dropped from the progress bar, rather than shown and left empty.
  const { client } = useClient();
  const { endSession } = useClientSession();
  const secondaryDocumentEnabled = client?.enableAdditionalDocuments ?? true;
  const steps = secondaryDocumentEnabled
    ? onboardSteps
    : onboardSteps.filter((step) => step.step !== OnBoardStep.SECONDARY_DOCUMENT_CAPTURE);
  const { id } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  /*
   * Where the applicant is *in the tracker*, which is not the same number as
   * where they are in the flow.
   *
   * currentStep is an OnBoardStep, numbered across the whole flow including the
   * secondary document. `steps` drops that entry when the client has not enabled
   * it, so from then on the two numberings diverge: handleNextStep jumps 1 → 3 to
   * skip it, and the tracker read 3 as its own third position — Review — marking
   * the selfie step done with a green tick the moment the document was captured,
   * before the selfie component had even mounted. Looking the step up by tag ties
   * the highlight to the step itself rather than to a shared index. An unknown
   * step yields 0, which highlights nothing rather than the wrong thing.
   */
  const trackerPosition = steps.findIndex((step) => step.step === currentStep) + 1;
  const [photoUrl, setPhotoUrl] = useState<string>();
  const [isExitModalOpen,setIsExitModalOpen] = useState(false);
  const [isRetryButtonDisabled, setIsRetryButtonDisabled] = useState(true);
  const [captureTimestamp, setCaptureTimeStamp] = useState({
    documentFront: 0,
    documentBack: 0,
    secondaryDocument: 0,
    selfie: 0,
    passiveLiveness: 0,
    neutral: 0,
    smileLiveness: 0,
  });
  const [imageFormat, setImageFormat] = useState({
    documentFront: "JPEG",
    documentBack: "JPEG",
    secondaryDocument: "JPEG",
    selfie: "JPEG",
    passiveLiveness: "JPEG",
    neutral: "JPEG",
    smileLiveness: "JPEG",
  });
  const [imageQuality,setImageQuality] = useState({
    documentFront: 0,
    documentBack: 0,
    secondaryDocument: 0,
    selfie: 0,
    passiveLiveness: 0,
    neutral: 0,
    smileLiveness: 0,
  })
  const [primaryDocumentImages,setPrimaryDocumentImages] = useState({
    frontImage:"",
    backImage:"",
  })

  const navigate = useNavigate();

  const handleNextStep = async () => {
    setPhotoUrl(undefined);
    setCurrentStep((prevStep) => {
      // Jump over the secondary-document step when it is not configured.
      const next =
        !secondaryDocumentEnabled && prevStep + 1 === OnBoardStep.SECONDARY_DOCUMENT_CAPTURE
          ? prevStep + 2
          : prevStep + 1;
      return Math.min(next, 4);
    });
  };

  const handleBackStep = () => {
    if (currentStep == 1) {
      navigate(`/`);
    }
    setPhotoUrl(undefined);
    setCurrentStep((prevStep) => {
      const previous =
        !secondaryDocumentEnabled && prevStep - 1 === OnBoardStep.SECONDARY_DOCUMENT_CAPTURE
          ? prevStep - 2
          : prevStep - 1;
      return Math.max(previous, 1);
    });
  };

  const handleContinueFaceDetection = () => {
    dispatchControlEvent(
      FaceCustomEvent.CONTROL,
      ControlEventInstruction.CONTINUE_DETECTION
    );
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' }); 
  }, [currentStep]);

  useEffect(() => {
    const handlePageShow = (event: any) => {
      if (event.persisted) {
        navigate(`/`);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const handleError = useCallback((error: Error) => {
    alert(error);
  }, []);

  const handleBackClick = () => {
    setPhotoUrl(undefined);
    setCurrentStep((prevStep) => Math.max(prevStep - 1, 1));
  };

  const {
    warningOpen,
    warningTimeLeft,
    handleExtendSession,
    handleSessionTimeout,
  } = useIdleSession();

  const renderStep = (currentStep: OnBoardStep) => {
    switch (currentStep) {
      case OnBoardStep.DOCUMENT_CAPTURE:
        return (
          <>
            <PrimaryDocumentCapture
              photoUrl={photoUrl}
              setPhotoUrl={setPhotoUrl}
              id={id}
              handleBackClick={handleBackClick}
              handleNextOnBoardStep={handleNextStep}
              handleOnBoardBackStep={handleBackStep}
              handleError={handleError}
              setCaptureTimeStamp={setCaptureTimeStamp}
              setImageFormat={setImageFormat}
              setImageQuality={setImageQuality}
              primaryDocumentImages={primaryDocumentImages}
              setPrimaryDocumentImages={setPrimaryDocumentImages}
            />
          </>
        );
      case OnBoardStep.SECONDARY_DOCUMENT_CAPTURE:
        return (
          <SecondaryDocumentCapture
            id={id}
            onError={handleError}
            photoUrl={photoUrl}
            setPhotoUrl={setPhotoUrl}
            handleNextOnBoardStep={handleNextStep}
            setCaptureTimeStamp={setCaptureTimeStamp}
            setImageFormat={setImageFormat}
            setImageQuality={setImageQuality}
          />
        );
      case OnBoardStep.SMILE_LIVENESS:
        return (
          <>
            <LivenessCapture
              id={id}
              photoUrl={photoUrl}
              setImageFormat={setImageFormat}
              setCaptureTimeStamp={setCaptureTimeStamp}
              setIsRetryButtonDisabled={setIsRetryButtonDisabled}
              setPhotoUrl={setPhotoUrl}
              handleBackClick={handleBackClick}
              handleNextOnBoardStep={handleNextStep}
              isRetryButtonDisabled={isRetryButtonDisabled}
              setImageQuality={setImageQuality}
            />
          </>
        );
      case OnBoardStep.RESULTS:
        return (
          <>
            <Results
              imageFormat={imageFormat}
              captureTimestamp={captureTimestamp}
              imageQuality={imageQuality}
              primaryDocumentImages={primaryDocumentImages}
              setIsExitModalOpen={setIsExitModalOpen}
            />
          </>
        );
      default:
        return (
          <PrimaryDocumentCapture
          photoUrl={photoUrl}
          setPhotoUrl={setPhotoUrl}
          id={id}
          handleBackClick={handleBackClick}
          handleNextOnBoardStep={handleNextStep}
          handleOnBoardBackStep={handleBackStep}
          handleError={handleError}
          setCaptureTimeStamp={setCaptureTimeStamp}
          setImageFormat={setImageFormat}
          setImageQuality={setImageQuality}
          primaryDocumentImages={primaryDocumentImages}
          setPrimaryDocumentImages={setPrimaryDocumentImages}
        />
        );
    }
  };

  return (
    <div className={styles.section}>
      <StepTracker steps={steps} currentStep={trackerPosition} />
      <CustomerDetailsProvider customerId={id} currentStep={currentStep}>
        <Canvas className={styles.canvasContainer}>
          <div className={styles.container}>
            {renderStep(currentStep)}
          </div>
        </Canvas>
      </CustomerDetailsProvider>
      <Modal 
        isOpen={isOpen}
        onClose={()=> setIsOpen(false)}
        actions={{continue:()=>{}}}
        type={modalType.SESSION_WARNING}
      />
      <Modal 
        isOpen={isExitModalOpen}
        onClose={()=>setIsExitModalOpen(false)}
        type={modalType.ON_EXIT}
        actions={{
          /*
           * End the whole journey, not just this application. The modal promises
           * that all progress is lost and the process restarts from the
           * beginning; navigating away while the resolved client survived left
           * the applicant on the email step, part-way through the journey they
           * had just abandoned.
           */
          continue: endSession
        }}
      />
    </div>
  );
};

export default Onboard;
